import { Graphics, FillGradient } from 'pixi.js'
import { stringToRawPath } from '~/lib/gsap/utils/paths'
import { buildOutlineSegmentsFromCenterlineRaw } from '~/lib/svg/brushUtils'
import { parseHexAlpha } from '~/lib/pixiColor'

// Compute gradient start/end in pixel space from CSS angle and pixel bounds.
// CSS: 0° = bottom-to-top, 90° = left-to-right.
// Gradient line passes through center and extends to cover the full box (per CSS spec).
// Also returns whether colorStops need reversing to counteract PixiJS's internal flip
// (PixiJS reverses the gradient texture when dx < 0 || dy < 0).
export const gradientEndpoints = (angle, minX, minY, maxX, maxY, colorStops) => {
	const rad = (angle * Math.PI) / 180
	const sin = Math.sin(rad),
		cos = Math.cos(rad)
	const cx = (minX + maxX) / 2,
		cy = (minY + maxY) / 2
	const w = maxX - minX,
		h = maxY - minY
	// CSS gradient line half-length: covers the full box diagonal projected onto the line
	const halfLen = (Math.abs(w * sin) + Math.abs(h * cos)) / 2
	const start = { x: cx - sin * halfLen, y: cy + cos * halfLen }
	const end = { x: cx + sin * halfLen, y: cy - cos * halfLen }
	// PixiJS flips the gradient texture when dx < 0 || dy < 0.
	// Counter this by pre-reversing colorStops when that would happen.
	const dx = end.x - start.x,
		dy = end.y - start.y
	const pixiWillFlip = dx < -1e-6 || dy < -1e-6
	const stops = pixiWillFlip ? colorStops.map(s => ({ offset: 1 - s.offset, color: s.color })).reverse() : colorStops
	return { start, end, colorStops: stops }
}

/**
 * PixiShape
 *
 * Graphics subclass for rect/circle/path objects.
 * Exposes rawPath getter/setter so GSAP MorphPlugin can tween paths directly.
 * Also exposes opacity, scaleX, scaleY, rotation aliases matching Redux prop names,
 * so transitionManager can tween Redux-named properties directly.
 *
 * _fill is a PixiJS fill style object: { color, alpha } for solid, { fill: FillGradient } for gradient.
 * PixiJS 8 Graphics.fill() accepts these objects directly.
 */
/** Signed area of a raw path segment (anchor points only). Positive = CCW in math coords. */
function segmentSignedArea(seg) {
	let area = 0
	// Anchor points: index 0,1 then every 6 after that (6,7 / 12,13 / ...)
	const pts = []
	pts.push(seg[0], seg[1])
	for (let i = 6; i < seg.length; i += 6) pts.push(seg[i], seg[i + 1])
	const n = pts.length / 2
	for (let i = 0; i < n; i++) {
		const j = (i + 1) % n
		area += pts[i * 2] * pts[j * 2 + 1] - pts[j * 2] * pts[i * 2 + 1]
	}
	return area / 2
}

class PixiShape extends Graphics {
	_rawPath = null
	_fill = { color: 0xcccccc, alpha: 1 }
	_stroke = null
	_strokeWidth = 1
	_isBrush = false
	_strokeCap = 'round'
	_fillAngle = 0 // gradient angle in CSS degrees (0=bottom-to-top, 90=left-to-right)
	_brushMorphParams = null // set during transitions to rebuild outline from centerline
	_rawPathVersion = 0 // lets mapped shader fields detect a live path edit without serializing on idle frames
	// The object's scale, applied to the points at draw time so a stroke keeps its width. `_rawPath` stays unscaled `data`.
	_drawScale = { x: 1, y: 1 }
	_unscaledPivot = { x: 0, y: 0 }
	_scaledCache = null

	/** The pivot in unscaled `data` units and the scale; Pixi's pivot is in the scaled points' units. */
	setDrawPose(pivot, sx, sy) {
		this._unscaledPivot = pivot
		this._drawScale = { x: sx, y: sy }
		this.pivot.set(pivot.x * sx, pivot.y * sy)
	}

	/** `_rawPath` with the draw scale applied, cached per path version and scale. */
	_drawPath() {
		const { x, y } = this._drawScale
		if (x === 1 && y === 1) return this._rawPath
		const c = this._scaledCache
		if (c && c.path === this._rawPath && c.version === this._rawPathVersion && c.x === x && c.y === y) return c.out
		const out = this._rawPath.map(seg => seg.map((v, i) => (i % 2 ? v * y : v * x)))
		this._scaledCache = { path: this._rawPath, version: this._rawPathVersion, x, y, out }
		return out
	}

	// Called by updatePixiObject to keep style in sync
	updateStyle(fill, stroke, strokeWidth) {
		this._fillAngle = fill?._angle || 0
		this._fill = fill || { color: 0xcccccc, alpha: 1 }
		this._stroke = stroke || null
		this._strokeWidth = strokeWidth || 1
		this._isBrush = false
	}

	// Called by updatePixiObject for brush objects
	updateBrushStyle(fill, strokeWidth, strokeCap) {
		this._isBrush = true
		this._fillAngle = fill?._angle || 0
		this._fill = fill || { color: 0xcccccc, alpha: 1 }
		this._strokeWidth = strokeWidth || 8
		this._strokeCap = strokeCap || 'round'
	}

	// Called by updatePixiObject to set path without triggering a redundant draw
	// (updatePixiObject calls _draw itself after setting _rawPath)
	setRawPathSilent(rawPath) {
		this._rawPath = rawPath
		this._rawPathVersion++
	}

	_draw() {
		if (!this._rawPath) return
		try {
			const path = this._drawPath()
			this.clear()
			this.context._fillRule = 'nonzero'
			const fill = this._fill || { color: 0xcccccc, alpha: 1 }
			const isGradient = fill.fill != null // { fill: FillGradient } vs { color, alpha }

			if (this._isBrush) {
				// Draw all segments
				for (const segment of path) {
					const l = segment.length
					this.moveTo(segment[0], segment[1])
					for (let i = 2; i < l; i += 6) {
						this.bezierCurveTo(
							segment[i],
							segment[i + 1],
							segment[i + 2],
							segment[i + 3],
							segment[i + 4],
							segment[i + 5]
						)
					}
					// Solid: fill per-quad to avoid earcut artifacts on overlapping quads
					if (!isGradient) this.fill(fill)
				}
				if (isGradient) {
					// textureSpace 'local' maps per-subpath, so use 'global' with pixel bounds
					let minX = Infinity,
						minY = Infinity,
						maxX = -Infinity,
						maxY = -Infinity
					for (const seg of path) {
						for (let i = 0; i < seg.length; i += 2) {
							if (seg[i] < minX) minX = seg[i]
							if (seg[i] > maxX) maxX = seg[i]
							if (seg[i + 1] < minY) minY = seg[i + 1]
							if (seg[i + 1] > maxY) maxY = seg[i + 1]
						}
					}
					const srcGrad = fill.fill
					const opts = {
						type: srcGrad.type,
						colorStops: srcGrad.colorStops,
						textureSpace: 'global',
						wrapMode: 'repeat',
					}
					if (srcGrad.type === 'radial') {
						const w = maxX - minX || 1,
							h = maxY - minY || 1
						opts.center = {
							x: minX + (srcGrad.center?.x ?? 0.5) * w,
							y: minY + (srcGrad.center?.y ?? 0.5) * h,
						}
						opts.outerCenter = {
							x: minX + (srcGrad.outerCenter?.x ?? 0.5) * w,
							y: minY + (srcGrad.outerCenter?.y ?? 0.5) * h,
						}
						opts.innerRadius = (srcGrad.innerRadius ?? 0) * Math.max(w, h)
						opts.outerRadius = (srcGrad.outerRadius ?? 0.5) * Math.max(w, h)
					} else {
						const ep = gradientEndpoints(this._fillAngle, minX, minY, maxX, maxY, srcGrad.colorStops)
						opts.start = ep.start
						opts.end = ep.end
						opts.colorStops = ep.colorStops
					}
					this.fill({ fill: new FillGradient(opts) })
				}
			} else if (isGradient) {
				// Gradient fill: use textureSpace 'global' with pixel bounds to avoid
				// aspect-ratio distortion that 'local' normalized coords cause.
				// First segment is outer shape, subsequent segments are holes (cut).
				let minX2 = Infinity,
					minY2 = Infinity,
					maxX2 = -Infinity,
					maxY2 = -Infinity
				for (const segment of path) {
					for (let i = 0; i < segment.length; i += 2) {
						if (segment[i] < minX2) minX2 = segment[i]
						if (segment[i] > maxX2) maxX2 = segment[i]
						if (segment[i + 1] < minY2) minY2 = segment[i + 1]
						if (segment[i + 1] > maxY2) maxY2 = segment[i + 1]
					}
				}
				const srcGrad2 = fill.fill
				const opts2 = {
					type: srcGrad2.type,
					colorStops: srcGrad2.colorStops,
					textureSpace: 'global',
					wrapMode: 'repeat',
				}
				if (srcGrad2.type === 'radial') {
					const w2 = maxX2 - minX2 || 1,
						h2 = maxY2 - minY2 || 1
					opts2.center = {
						x: minX2 + (srcGrad2.center?.x ?? 0.5) * w2,
						y: minY2 + (srcGrad2.center?.y ?? 0.5) * h2,
					}
					opts2.outerCenter = {
						x: minX2 + (srcGrad2.outerCenter?.x ?? 0.5) * w2,
						y: minY2 + (srcGrad2.outerCenter?.y ?? 0.5) * h2,
					}
					opts2.innerRadius = (srcGrad2.innerRadius ?? 0) * Math.max(w2, h2)
					opts2.outerRadius = (srcGrad2.outerRadius ?? 0.5) * Math.max(w2, h2)
				} else {
					const ep = gradientEndpoints(this._fillAngle, minX2, minY2, maxX2, maxY2, srcGrad2.colorStops)
					opts2.start = ep.start
					opts2.end = ep.end
					opts2.colorStops = ep.colorStops
				}
				let firstSign2 = 0
				for (const segment of path) {
					this.moveTo(segment[0], segment[1])
					for (let i = 2; i < segment.length; i += 6) {
						this.bezierCurveTo(
							segment[i],
							segment[i + 1],
							segment[i + 2],
							segment[i + 3],
							segment[i + 4],
							segment[i + 5]
						)
					}
					if (firstSign2 === 0) {
						firstSign2 = Math.sign(segmentSignedArea(segment)) || 1
						this.fill({ fill: new FillGradient(opts2) })
						if (this._stroke) {
							this.stroke({ color: this._stroke, width: this._strokeWidth || 1 })
						}
					} else {
						const sign2 = Math.sign(segmentSignedArea(segment)) || firstSign2
						if (sign2 === firstSign2) {
							this.fill({ fill: new FillGradient(opts2) })
						} else {
							this.cut()
						}
					}
				}
			} else {
				// Solid fill. Subsequent segments are holes (cut) or islands (fill),
				// determined by winding: same sign as first = island, opposite = hole.
				const solidFill = { color: fill.color ?? 0xcccccc }
				if (fill.alpha != null && fill.alpha < 1) solidFill.alpha = fill.alpha
				let firstSign = 0
				for (const segment of path) {
					const l = segment.length
					if (l < 8) continue
					this.moveTo(segment[0], segment[1])
					for (let i = 2; i < l; i += 6) {
						this.bezierCurveTo(
							segment[i],
							segment[i + 1],
							segment[i + 2],
							segment[i + 3],
							segment[i + 4],
							segment[i + 5]
						)
					}
					if (firstSign === 0) {
						firstSign = Math.sign(segmentSignedArea(segment)) || 1
						this.fill(solidFill)
						if (this._stroke) {
							this.stroke({ color: this._stroke, width: this._strokeWidth || 1 })
						}
					} else {
						const sign = Math.sign(segmentSignedArea(segment)) || firstSign
						if (sign === firstSign) {
							// Island: same winding as outer — fill on top
							this.fill(solidFill)
						} else {
							// Hole: opposite winding — cut out
							this.cut()
						}
					}
				}
			}
		} catch {
			// draw error suppressed — shape data may be temporarily invalid during edits
		}
	}

	// --- rawPath: interface for MorphPlugin ---

	get rawPath() {
		return this._rawPath
	}

	set rawPath(value) {
		this._rawPathVersion++
		if (this._brushMorphParams) {
			// During transition: value is the interpolated centerline rawPath.
			// Rebuild the outline from it and draw that.
			const { strokeWidth, strokeCap, minWidth, widthProfile } = this._brushMorphParams
			const outline = buildOutlineSegmentsFromCenterlineRaw(value, strokeWidth, strokeCap, minWidth, widthProfile)
			this._rawPath = outline || value
			this._draw()
			return
		}
		this._rawPath = value
		this._draw()
	}

	// --- Property aliases for GSAP (match Redux prop names) ---

	get opacity() {
		return this.alpha * 100
	}

	set opacity(v) {
		this.alpha = v / 100
	}

	get scaleX() {
		return this._drawScale.x
	}

	set scaleX(v) {
		this.setDrawPose(this._unscaledPivot, v, this._drawScale.y)
		this._draw()
	}

	get scaleY() {
		return this._drawScale.y
	}

	set scaleY(v) {
		this.setDrawPose(this._unscaledPivot, this._drawScale.x, v)
		this._draw()
	}

	// Set fill directly from a PixiJS fill style object during transitions
	setFillDirect(pixiFill) {
		this._fill = pixiFill
		this._fillAngle = pixiFill?._angle || 0
		this._draw()
	}

	// --- Color aliases for GSAP tweening ---
	// GSAP tweens hex strings, so getter returns hex and setter accepts hex.
	// For gradient fills, GSAP can't interpolate — transitions fall back to instant swap.

	get fillColor() {
		const f = this._fill
		if (!f) return '#cccccc'
		if (f.fill) return '#cccccc' // gradient — not tweenable
		const hex = '#' + (f.color || 0).toString(16).padStart(6, '0')
		if (f.alpha != null && f.alpha < 1) {
			return (
				hex +
				Math.round(f.alpha * 255)
					.toString(16)
					.padStart(2, '0')
			)
		}
		return hex
	}

	set fillColor(v) {
		// GSAP passes interpolated hex string during tween
		if (typeof v === 'string') {
			this._fill = parseHexAlpha(v)
		} else {
			this._fill = v
		}
		this._draw()
	}

	get strokeColor() {
		return this._stroke
	}

	set strokeColor(v) {
		this._stroke = v
		this._draw()
	}

	// Width alias for GSAP. Without it the tween target is undefined and GSAP warns
	// ("Invalid property strokeWidth ... Missing plugin?") and skips the prop, so width
	// snaps at the end of the morph instead of animating. Named like the Redux prop;
	// _strokeWidth stays private to avoid shadowing anything on Graphics.
	get strokeWidth() {
		return this._strokeWidth
	}

	set strokeWidth(v) {
		this._strokeWidth = v
		this._draw()
	}
}

export { stringToRawPath }
export default PixiShape
